import React from "react";
import { Container, Typography, Button, Box } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { reportClientError } from "../../lib/errorReporter";
import { hideNativeSplash } from "../../native/splashScreen";

const generateErrorId = () =>
  `ERR-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * How many consecutive catches we allow before withdrawing "Try Again".
 *
 * One retry is worth offering: transient causes (a race, a half-written cache
 * entry, a request that failed once) really do clear on a fresh mount. A second
 * catch with nothing rendering in between is evidence the fault is
 * deterministic, and a third attempt would land the user on this same screen.
 * At that point the only honest control left is "Go to Homepage", which
 * recovers because it does a full document reload.
 */
export const MAX_CONSECUTIVE_FAILURES = 2;

/**
 * Identity of an *underlying* fault, not of an attempt.
 *
 * Two catches sharing a message and a component stack are the same bug hit
 * twice, so they must share one Error ID and produce one crash report. The
 * stack is normalised because it is a formatted string; leading/trailing
 * whitespace is presentation, not identity.
 */
const errorSignature = (error, componentStack) => {
  const message =
    (error && (error.message || String(error))) || "Unknown client error";
  return `${message}::${String(componentStack || "").trim()}`;
};

/**
 * Did a dynamic `import()` fail to fetch, rather than our own code throwing?
 *
 * Webpack rejects with an Error whose `name` is `ChunkLoadError`, which is the
 * primary test. The message pattern is a fallback only: the name is dropped
 * when the rejection is re-wrapped, and it is missing entirely from a few
 * older runtimes, but the message text has been stable across webpack 4 and 5.
 */
const isChunkLoadError = (error) => {
  if (!error) return false;
  if (error.name === "ChunkLoadError") return true;
  const message = error.message || String(error);
  return /Loading (CSS )?chunk .* failed/i.test(message);
};

/**
 * Catches render-time crashes below it and offers a way out.
 *
 * WHY RETRY REMOUNTS
 * ------------------
 * Clearing `hasError` and returning `this.props.children` unchanged is not a
 * retry: the children, their props and the application state that just threw
 * are all identical, so a deterministic fault throws again on the very next
 * render. Retry therefore bumps `resetKey`, which is the `key` on the fragment
 * wrapping the children — React tears the subtree down and builds new
 * instances, so anything that was mounted re-runs its effects and re-fetches.
 *
 * A remount cannot fix state that lives *above* this boundary, or data on the
 * server that is itself malformed. `onRetry` is the seam for that: an owning
 * screen can pass a callback to refetch or reset before the subtree rebuilds.
 * It is optional — the boundary is self-sufficient without it, and the app root
 * in `App.js` does not pass one.
 *
 * WHY THE ERROR ID IS STICKY
 * --------------------------
 * The Error ID on screen exists so a support message and a stored crash row can
 * be matched up. Minting a new one per attempt breaks exactly that: the user
 * quotes an id that is no longer the one they were shown, and one bug shows up
 * in the admin crash list as several distinct failures. The id is keyed to the
 * fault's signature and held for the life of this mount, and the report is sent
 * only the first time that signature is seen.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      // Consecutive catches with no successful render of the children in
      // between. Reset once the subtree renders again (see componentDidUpdate).
      failureCount: 0,
      // Bumping this remounts the children instead of re-rendering them.
      resetKey: 0,
    };
    // signature -> errorId. Both the stable-id source and the report dedupe
    // ledger, for the life of this mount. A page reload starts a new incident,
    // which is correct: it is a new session for support to correlate against.
    this.seenErrors = new Map();
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const componentStack = errorInfo?.componentStack;
    const signature = errorSignature(error, componentStack);
    const alreadySeen = this.seenErrors.has(signature);
    const errorId = alreadySeen
      ? this.seenErrors.get(signature)
      : generateErrorId();
    if (!alreadySeen) this.seenErrors.set(signature, errorId);

    console.error(`[ErrorBoundary ${errorId}]`, error, errorInfo);

    // This fallback replaces its children, so on native it has just unmounted
    // SplashGate. The failsafe still fires on its own, but waiting up to
    // SPLASH_FAILSAFE_MS would leave the splash covering — and swallowing taps
    // on — the retry button below. Take it down now instead.
    hideNativeSplash();
    this.setState((prev) => ({
      error,
      errorInfo,
      errorId,
      failureCount: prev.failureCount + 1,
    }));

    // One row per distinct fault per mount. Retrying a crash is a user action,
    // not new information, and the server-side limiter is a flood backstop
    // rather than a reason to send duplicates at it.
    if (alreadySeen) return;
    reportClientError({
      error,
      componentStack,
      errorId,
      source: "error_boundary",
    });
  }

  componentDidUpdate(prevProps, prevState) {
    // The children rendered without throwing after a catch — the retry worked.
    // "Consecutive" has to mean consecutive, otherwise an unrelated crash later
    // in the session would inherit a spent budget and open with no retry
    // offered at all.
    if (prevState.hasError && !this.state.hasError && this.state.failureCount) {
      this.setState({ failureCount: 0 });
    }
  }

  handleRetry = () => {
    // Let an owning screen refetch or reset first, then rebuild the subtree.
    // Optional by design: without it the remount alone is still a real retry.
    if (typeof this.props.onRetry === "function") {
      try {
        this.props.onRetry();
      } catch (err) {
        // A throwing onRetry must not strand the user on the crash screen with
        // a button that now does nothing at all.
        console.error("[ErrorBoundary] onRetry threw", err);
      }
    }

    // A remount cannot recover a failed chunk fetch. React caches the rejected
    // `lazy()` payload for the life of the document, so the rebuilt subtree
    // re-throws the identical error, the second catch spends the last of
    // MAX_CONSECUTIVE_FAILURES, and the button this user just pressed is
    // withdrawn without ever having re-requested anything. Only a fresh
    // document re-runs the import. `onRetry` still ran above, so a caller that
    // clears persistent state gets to do so before the page goes away.
    //
    // Skipped when the browser reports itself offline: reloading with no
    // network replaces this screen with the browser's own error page, and what
    // is lost with it is the Error ID the copy below asks the user to quote.
    // Note it is NOT an escape route being protected — "Go to Homepage" sets
    // `location.href` and there is no service worker in this app, so that
    // button is exactly as dead offline as a reload. `!== false` rather than a
    // truthiness test because a runtime that does not implement `onLine`
    // leaves it undefined, and "unknown" must behave like "online" or the fix
    // never fires there.
    //
    // A consequence, deliberately accepted: `failureCount` resets with the new
    // document, so the MAX_CONSECUTIVE_FAILURES budget can never be spent for
    // a chunk error and the "Retrying didn't clear this one" copy is
    // unreachable for this class. Each reload costs one deliberate user tap —
    // the same as pressing F5 — so bounding it would be taking away a retry
    // the user keeps choosing. It does mean N taps file N crash reports with N
    // Error IDs rather than one deduped row; see the note on `seenErrors`.
    if (
      isChunkLoadError(this.state.error) &&
      typeof navigator !== "undefined" &&
      navigator.onLine !== false
    ) {
      window.location.reload();
      return;
    }

    this.setState((prev) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      resetKey: prev.resetKey + 1,
    }));
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, errorId: null });
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      const canRetry = this.state.failureCount < MAX_CONSECUTIVE_FAILURES;

      return (
        <Container maxWidth="md">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "80vh",
              textAlign: "center",
              gap: 3,
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 80, color: "error.main" }} />
            <Typography variant="h3" color="error">
              Oops! Something went wrong
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {canRetry
                ? "We're sorry for the inconvenience. The application encountered an unexpected error."
                : "We're sorry for the inconvenience. Retrying didn't clear this one, so please head back to the homepage — and quote the Error ID below if you contact support."}
            </Typography>
            {this.state.errorId && (
              <Typography variant="caption" color="text.secondary">
                Error ID: {this.state.errorId}
              </Typography>
            )}
            {process.env.NODE_ENV === "development" && this.state.error && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: "grey.100",
                  borderRadius: 1,
                  textAlign: "left",
                  width: "100%",
                  maxWidth: "600px",
                }}
              >
                <Typography variant="caption" component="pre">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center" }}>
              {canRetry && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleRetry}
                >
                  Try Again
                </Button>
              )}
              <Button
                variant={canRetry ? "outlined" : "contained"}
                color="primary"
                size="large"
                onClick={this.handleGoHome}
              >
                Go to Homepage
              </Button>
            </Box>
          </Box>
        </Container>
      );
    }

    // Keyed so that a retry unmounts the previous subtree outright. Without the
    // key this branch hands React the same element tree it just rendered.
    return (
      <React.Fragment key={this.state.resetKey}>
        {this.props.children}
      </React.Fragment>
    );
  }
}

export default ErrorBoundary;
